/* eslint-disable react/prop-types */
import React, { Suspense } from 'react';
import cx from 'classnames';
import { Shelf, Icon, Text, Stack, Button, TextLink } from '@chaser/components';
import style from './OnboardingChecklist.module.scss';
import useFetch from '../../hooks/useFetch';
import SettingsService from '../../services/settings-service';
import emitter from '../../util/emitter';
import { useHistory } from 'react-router';
import { Actions } from '../../hooks/useCompleteAction';
import { useRef } from 'react';
import { useLayoutEffect } from 'react';
import useOrganisation from '../../hooks/useOrganisation';
import differenceInDays from 'date-fns/differenceInDays';
import { useDispatch, useSelector } from 'react-redux';
import { SET_ONBOARDING_CHECKLIST_SHOWN } from '../../duck/types';
import { useRouteMatch } from 'react-router-dom';

function roundToFraction(value, fraction) {
  let x = value / fraction;
  x = Math.round(x);
  x *= fraction;
  return x;
}

function getRect(el) {
  return el.getBoundingClientRect();
}

function flip(firstRect, lastEl) {
  const lastRect = getRect(lastEl);

  const dx = lastRect.x - firstRect.x;
  const dy = lastRect.y - firstRect.y;
  const dw = lastRect.width / firstRect.width;
  const dh = lastRect.height / firstRect.height;

  // (so CSS knows it's being flipped)
  // data-flipping="true"
  lastEl.dataset.flipping = true;

  lastEl.style.setProperty('--dx', dx);
  lastEl.style.setProperty('--dy', dy);
  lastEl.style.setProperty('--dw', dw);
  lastEl.style.setProperty('--dh', dh);

  requestAnimationFrame(() => {
    delete lastEl.dataset.flipping;
  });
}

const OnboardingChecklistInner = ({ shortCode }) => {
  const ref = useRef();
  const flipRect = useRef();
  const history = useHistory();
  const dispatch = useDispatch();
  const open = useSelector(state => state.navigationReducer.onboardingChecklistShown);
  const { data: completed } = useFetch(SettingsService.getActionsCompleted, shortCode);
  let setNextUp = false;
  let completedAmount = 0;

  function toggleOpen(beOpen = !open) {
    if (ref.current) {
      flipRect.current = getRect(ref.current);
    }
    dispatch({ type: SET_ONBOARDING_CHECKLIST_SHOWN, shown: beOpen });
  }

  useLayoutEffect(() => {
    if (!flipRect.current || !ref.current) return;
    flip(flipRect.current, ref.current);
    flipRect.current = null;
  }, [open]);

  const welcomeMatch = useRouteMatch({
    path: ['/addorganisation/csvconfiguration', '/select-organisations'],
    exact: false,
  });

  const actionItems = [
    {
      key: Actions.EDIT_SIGNATURE,
      label: 'Add signature',
      action: () => history.push('/receivables/manage/email'),
    },
    {
      key: Actions.EDIT_STATEMENT,
      label: 'Edit customer statement',
      action: () => history.push('/receivables/manage#customer-statements'),
    },
    {
      key: Actions.SET_CHASE_TIME,
      label: 'Set your chase time',
      action: () => history.push('/receivables/manage/timings'),
    },
    {
      key: Actions.REVIEW_TEMPLATES,
      label: 'Review templates',
      action: () => history.push('/receivables/manage/templates'),
    },
    {
      key: Actions.ADD_SCHEDULE,
      label: 'Add new schedule',
      action: () => history.push('/receivables/manage/schedules'),
    },
    {
      key: Actions.START_CHASING,
      label: 'Start chasing',
      action: () => emitter.emit('openStartChasing'),
    },
  ];

  actionItems.forEach(item => {
    if (completed[item.key]) completedAmount++;

    if (!setNextUp && !completed[item.key]) {
      item.nextUp = true;
      setNextUp = true;
    }
  });

  const todoAmount = actionItems.length - completedAmount;
  const allDone = todoAmount === 0;

  useLayoutEffect(() => {
    if (allDone) {
      dispatch({ type: SET_ONBOARDING_CHECKLIST_SHOWN, shown: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!completed || welcomeMatch) return null;

  const completedPerc = roundToFraction(completedAmount / actionItems.length, 0.01);

  return (
    (!allDone || open) && (
      <div className={cx(style.card, open && style.isOpen)} ref={ref}>
        <button
          type="button"
          className={style.closedContent}
          onClick={() => {
            toggleOpen(true);
          }}
        >
          <div className={style.openIcon}>
            <Icon name="tick" color="Dark Neutral 1" size={16} />
          </div>
          <div className={style.notification}>{todoAmount}</div>
        </button>

        <div className={cx(style.openContent, allDone && style.allDone)}>
          <Stack className={style.list}>
            <Shelf justifyContent="space-between">
              <Text fontSize="medium" fontWeight="bold">
                Getting started
              </Text>
              <Button
                icon={<Icon name="arrow-down" size={10} />}
                appearance="minimal"
                size="narrow"
                onClick={() => {
                  toggleOpen(false);
                }}
                aria-label="Close"
              />
            </Shelf>
            <Shelf>
              <Text color="Blue" fontSize="medium" fontWeight="bold">
                {completedPerc * 100}%
              </Text>
              <div className={style.progress} style={{ '--perc': completedPerc }} />
            </Shelf>
            <Stack gap="8px">
              {actionItems.map(item => (
                <Shelf
                  gap="xsmall"
                  key={item.key}
                  className={cx(completed[item.key] && style.completed)}
                >
                  <span className={style.check}>
                    <Icon name="tick" color="Green" size={16} />
                  </span>
                  <Text
                    as="button"
                    type="button"
                    className={style.actionButton}
                    trim={false}
                    onClick={() => {
                      item.action();
                    }}
                    color={item.nextUp ? 'Dark Neutral 1' : 'Mid Neutral 1'}
                    fontWeight={item.nextUp ? 'bold' : 'regular'}
                  >
                    {item.label}
                    <Icon className={style.arrow} name="chevron-right" size={12} />
                  </Text>
                </Shelf>
              ))}
            </Stack>
          </Stack>
          <Stack className={style.done} gap={50}>
            <Stack>
              <Text fontSize={29}>
                <span role="img" aria-label="tada">
                  🎉
                </span>
              </Text>
              <Text fontSize="medium" fontWeight="bold">
                All done!
              </Text>
              <Text color="Mid Neutral 1">You’re all set up</Text>
              <Button
                onClick={() => {
                  toggleOpen(false);
                }}
              >
                Finish
              </Button>
            </Stack>
            <Text trim={false} color="Mid Neutral 1">
              <TextLink
                as="button"
                type="button"
                onClick={() => {
                  toggleOpen(false);
                  emitter.emit('openSupportModal');
                }}
                monochrome
              >
                Got any questions?
              </TextLink>
            </Text>
          </Stack>
        </div>
      </div>
    )
  );
};

const OnboardingChecklist = () => {
  const { createdAt, ShortCode } = useOrganisation(null, {
    suspense: false,
  });

  if (!createdAt || !ShortCode) return null;

  const createdDate = new Date(createdAt);
  const diff = differenceInDays(new Date(), createdDate);

  if (diff > 14) return null;
  return (
    <Suspense fallback={<div />}>
      <OnboardingChecklistInner shortCode={ShortCode} />
    </Suspense>
  );
};

export default OnboardingChecklist;
